import { loadStripe } from '@stripe/stripe-js';
import apiClient from './apiClient';
import { Order } from '../types/cart.types';

// Utilisation des variables d'environnement pour la clé publique Stripe
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RALVIJdaH5TMNaHtPCbNAya0IPskKnyPx9D7e8ZUmgBJQG6ZXBGcDp1ifgmPVG9iI0ikRUmQZWXm4MxcfE3OoB600txHNevf4';

class StripeService {
  stripePromise: Promise<any>;
  
  constructor() {
    this.stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  }

  /**
   * Crée une session de paiement Stripe
   * @param cartItems Éléments du panier
   * @param totalAmount Montant total
   * @param successUrl URL de redirection en cas de succès
   * @param cancelUrl URL de redirection en cas d'annulation
   */
  async createPaymentSession(
    cartItems: Array<{ id: string; packName: string; quantity: number; unitPrice: number; }>,
    totalAmount: number,
    successUrl: string,
    cancelUrl: string
  ) {
    try {
      // Cette partie devra être implémentée côté backend
      // Simulons une réponse pour l'instant
      /*
      const response = await apiClient.post('/payments/create-session', {
        items: cartItems,
        amount: totalAmount,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      return response.data;
      */

      // Données simulées
      return {
        sessionId: 'cs_test_' + Math.random().toString(36).substring(2, 15),
        publicKey: STRIPE_PUBLIC_KEY
      };
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw error;
    }
  }

  /**
   * Redirige l'utilisateur vers la page de paiement Stripe
   * @param sessionId ID de la session de paiement
   */
  async redirectToCheckout(sessionId: string): Promise<void> {
    try {
      const stripe = await this.stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId
      });
      
      if (error) {
        console.error('Error redirecting to checkout:', error);
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error in redirectToCheckout:', error);
      throw error;
    }
  }

  /**
   * Vérifie le statut d'un paiement
   * @param sessionId ID de la session de paiement
   */
  async checkPaymentStatus(sessionId: string): Promise<'success' | 'pending' | 'failed'> {
    try {
      const response = await apiClient.get(`/payments/status/${sessionId}`);
      return response.data.status;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return 'failed';
    }
  }

  /**
   * Crée un PaymentIntent pour les paiements manuels
   * @param amount Montant en centimes
   * @param currency Devise (par défaut EUR)
   */
  async createPaymentIntent(amount: number, currency: string = 'eur'): Promise<{ clientSecret: string, id: string, paymentId: number }> {
    try {
      const response = await apiClient.post(`/v1/payments/init?amount=${amount}&currency=${currency}`, {
        amount: amount, // Convertir en centimes
        method: 'CARD',
        type: 'ADD_BALANCE',
        currency,
        /*automatic_payment_methods: {
          enabled: true,
        },*/
      });
      if(  response.status!==200 && response.status!==201 ) {
        throw new Error('Client secret not returned from payment intent creation');
      }
      //var stripe=await stripeApiClient.post(`/v1/payment_intents?amount=${Math.round(amount * 100).toString()}&currency=${currency.toLowerCase()}`)
      return {'clientSecret':response.data.id , 'id':response.data.clientSecret, 'paymentId': response.data.paymentId};
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }


/**
 * Init paiement par carte + création PaymentIntent Stripe
 * @param amount Montant en euros
 * @param currency Devise (par défaut EUR)
 */
async createVoucherViaCard(
  amount: number,
  currency: string = 'eur'
): Promise<{ clientSecret: string; paymentIntentId: string }> {

  // 1. Création paiement BACK
  const response = await apiClient.post(`/v1/payments/init?amount=${amount}&currency=${currency}`, {
    amount:amount,
    method: 'CARD',
    type: 'NEW_VOUCHER',
    currency
  });

  if (response.status !== 200 && response.status !== 201) {
    throw new Error('Payment init failed');
  }

  // 2. Création PaymentIntent Stripe
  /*const stripe = await stripeApiClient.post(
    `/v1/payment_intents?amount=${Math.round(amount * 100)}&currency=${currency}`
  );*/

  if (!response.data?.clientSecret || !response.data?.id) {
    throw new Error('Stripe PaymentIntent creation failed');
  }

  return {
    clientSecret: response.data.id ,
    paymentIntentId: response.data.clientSecret 
  };
}


/**
 * Crée les vouchers côté BACK après validation du paiement
 * @param packId ID du pack à acheter
 * @param quantity Nombre de vouchers
 * @param paymentIntentId ID du PaymentIntent Stripe
 */
  async finalizeVoucherCreation(
    packs: { packId: number; quantity: number;  }[],
    paymentIntentId: string,
    description: string
  ) {
    const response = await apiClient.post('/v1/vouchers/create/card', {
      packs,
      paymentIntentId,
      description
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Échec de la création des vouchers');
    }

    return response.data; // retourne la liste des VoucherModel
  }

 
  async completeOrder(paymentIntentId: string, id: number, externalReference:string): Promise<Order> {
    try {
      
      const response = await apiClient.post('/v1/payments/finish', {
        paymentIntentId,
        id,
        externalReference
      });
      if(  response.status!==200 && response.status!==201 ) {
        throw new Error('Error completing order');
      }
      return response.data;
      
    } catch (error) {
      console.error('Error completing order:', error);
      throw error;
    }
  }
}

export const stripeService = new StripeService();
